import { State, TestingLocation } from '@aicpcu/web-api-spec-model';

import { DOMESTIC_COUNTRY_VALUE } from '@app/constants/address';
import { FormValues } from '@app/interfaces/form';
import { LabelValue } from '@app/interfaces/labelValue';
import { apiService } from '@app/services/api.service';
import { webApi } from '@app/services/web-api';
import { byLabel, getAlphaOrder } from '@app/utility/sort';

import { ExamDataOption } from './purchase-form.types';
import { formUtil } from './purchase-form.util';

/**
 * Get country options from testing location data.
 *
 * @param {TestingLocation[]} testingLocationData List of testing location data.
 *
 * @returns {LabelValue[]} Country options mapped to select options.
 */
export const getCountryOptions = (
	testingLocationData: readonly TestingLocation[],
): LabelValue[] => {
	if (!testingLocationData) {
		return [];
	}

	const uniqueCountries = testingLocationData
		.map(({ country = DOMESTIC_COUNTRY_VALUE }) => country)
		.filter((value, index, array) => array.indexOf(value) === index)
		.sort(getAlphaOrder);

	const countryOptions = uniqueCountries.map<LabelValue>((country) => ({
		label: country,
		value: country,
	}));

	return countryOptions;
};

/**
 * Get state options from testing location data by making a web api request.
 *
 * @param {TestingLocation[]} testingLocationData List of testing location data.
 * @callback setApiError Sets an api error if necessary.
 *
 * @returns {Promise<LabelValue[]>} State options mapped to select options.
 */
export const getStateOptions = async (
	locations: readonly TestingLocation[],
	setApiError: any,
): Promise<LabelValue[]> => {
	let data: readonly State[];
	try {
		data = await webApi.getStates();
	} catch (e) {
		return setApiError(e);
	}

	const uniqueStates = locations
		.map((location) => location.state || '')
		.filter((value, index, array) => value && array.indexOf(value) === index)
		.sort(getAlphaOrder);

	const stateOptions = uniqueStates.map<LabelValue>((location) => {
		const stateItem = data.find(
			({ abbreviation = '' }) => abbreviation === location,
		);
		return {
			label: stateItem?.name!,
			value: location,
		};
	});

	return stateOptions;
};

/**
 * Get testing center options by location selections.
 *
 * @param {TestingLocation[]} locations List of testing location data.
 * @param {string} Country Country code.
 * @param {string=} usState US state code.
 *
 * @returns {ExamDataOption[]} Center options.
 */
export const getTestingCenterOptions = (
	locations: readonly TestingLocation[],
	country: string,
	usState: string = '',
): ExamDataOption[] => {
	if (!locations) {
		return [];
	}

	return country === DOMESTIC_COUNTRY_VALUE
		? locations
				.filter(({ state }) => state && state === usState)
				.map<ExamDataOption>(
					({ description: label = '', note = '', siteId: value = '' }) => ({
						label,
						note,
						value,
					}),
				)
				.sort(byLabel)
		: locations
				.filter(
					({ country: locCountry = DOMESTIC_COUNTRY_VALUE }) =>
						locCountry === country,
				)
				.map<ExamDataOption>(
					({ description: label = '', note = '', siteId: value = '' }) => ({
						label,
						note,
						value,
					}),
				)
				.sort(byLabel);
};

/**
 * Get testing center location note from location options and siteId.
 *
 * @param {ExamDataOption[]} centerOptions Testing center location options.
 * @param {string} siteId Site ID of selected center.
 *
 * @returns {string} Testing location note.
 */
export const getTestingLocationNote = (
	centerOptions: ExamDataOption[],
	siteId: string,
): string => {
	const selectedLocation = centerOptions.find(({ value }) => siteId === value);
	return selectedLocation?.note || '';
};

/**
 * Get program options from course code by making a web api request.
 *
 * @param {string} courseCode Course code.
 * @callback setApiError Sets an api error if necessary.
 */
export const getProgramOptions = async (
	courseCode: string,
	setApiError: any,
): Promise<LabelValue[]> => {
	const response = await apiService.product.programCourse.findByCourse(
		courseCode,
	);
	const { status, data = {} } = response;

	if (status !== 200) {
		return setApiError({
			...data,
			api: 'program',
			errorType: 'inline',
		});
	}

	const {
		values: programOptions,
	}: {
		values: {
			programCode: string;
			abbreviation: string;
		}[];
	} = data;

	return programOptions
		.map<LabelValue>(
			({ programCode: value = '', abbreviation: label = '' }) => ({
				label,
				value,
			}),
		)
		.filter(({ label }) => label)
		.sort(byLabel);
};

/**
 * Get label from form value.
 *
 * @param {string} formValue Form input values.
 * @param {LabelValue[]} options
 */
export const getLabel = (
	formValues: FormValues,
	fieldName: string | undefined,
	options: LabelValue[],
) => {
	const option = options.find(
		({ value }) =>
			fieldName && formUtil.selectValue(formValues, fieldName) === value,
	);
	return option?.label || '';
};
